import type { Thought } from '@/types'

const img = (name: string): string => `/images/${name}`

export const thoughts: readonly Thought[] = [
    {
        slug: 'beyond-ai-aesthetics-what-human-led-digital-art-means-now',
        date: '2025-12-30',
        dateLabel: 'December 30, 2025',
        title: 'Beyond AI Aesthetics: What Human-Led Digital Art Means Now',
        excerpt:
            'AI didn’t replace creativity. It reshaped it — forcing artists to redefine what human originality truly means.',
        author: 'Jayson Castle',
        authorPhoto: img('aB5tmr7n4kg9KC9uwroe7fpx4.jpg'),
        authorRole: 'Visual Artist / Creator',
        image: img('thought-human-hand.jpg'),
        readMinutes: '7',
        topic: 'Practice',
        body: [
            {
                paragraphs: [
                    'The argument about whether a machine can make art was settled by boredom rather than by proof. Once everyone had seen ten thousand generated images, the question stopped being interesting and a better one arrived in its place: what is left that only a person would bother to do?',
                    'That question is more useful because it has a practical answer. A model produces the average of everything it has seen. A person produces the consequence of everything they have refused. The refusal is the work.'
                ]
            },
            {
                heading: 'Originality was never about novelty',
                paragraphs: [
                    'For most of the last century originality was measured by whether a thing had been seen before. That measure is now worthless, because novelty is cheap and infinite. What remains expensive is commitment: the willingness to stay with one narrow idea long enough that it begins to resist you.',
                    'Human-led work reads differently for a simple reason. Decisions accumulate. You can feel where somebody changed their mind, where they kept something that did not quite work because removing it would have been dishonest.'
                ]
            },
            {
                heading: 'Using the tool without becoming it',
                paragraphs: [
                    'The studios producing the strongest work are not avoiding these tools. They are putting them somewhere specific and keeping them there: generating variations nobody will see, testing a composition at three in the morning, filling in the tedious middle of a long sequence.',
                    'What they do not do is let the tool make the final call. The last decision stays with a person, because the last decision is the one that carries a point of view.'
                ]
            },
            {
                heading: 'What this means for how we work now',
                paragraphs: [
                    'Process becomes the artefact. When the output is trivially reproducible, the record of how it came to exist is the part that cannot be copied.',
                    'That is why more artists are publishing their failures alongside their finished pieces. Not as humility, and not as content, but because the failures are the only reliable proof that a human was ever in the room.'
                ]
            }
        ]
    },
    {
        slug: 'when-images-begin-to-listen-the-quiet-power-of-responsive-art',
        date: '2025-10-29',
        dateLabel: 'October 29, 2025',
        title: 'When Images Begin to Listen: The Quiet Power of Responsive Art',
        excerpt:
            'Interactive work is evolving beyond touchscreens and gimmicks. The most compelling contemporary pieces now respond subtly — to sound, environment, presence, and emotion.',
        author: 'Hanna Jane Winston',
        authorPhoto: img('IXoZk0GX4b82wzbVdgUq07pXs.jpg'),
        authorRole: 'Writer / Curator',
        image: img('thought-responsive-light.jpg'),
        readMinutes: '6',
        topic: 'Interaction',
        body: [
            {
                paragraphs: [
                    'The first generation of interactive art asked you to do something. Press here, wave there, and watch the piece perform its trick. It was generous and it was exhausting, and almost none of it survived a second visit.',
                    'The work being made now asks for nothing at all. It notices you, adjusts by a degree, and lets you decide whether you noticed back.'
                ]
            },
            {
                heading: 'Response below the threshold',
                paragraphs: [
                    'The shift is one of scale. Instead of a visible cause and a visible effect, these pieces operate just under the point where you would call it a reaction. A room tone that follows the number of people in it. A surface that dims when the street outside goes quiet.',
                    'Nothing announces itself, which is precisely why it holds. You stay longer because you are not sure what changed.'
                ]
            },
            {
                heading: 'The gallery is a sensor',
                paragraphs: [
                    'Environmental input has replaced the button. Temperature, footfall, ambient sound and time of day are all doing the work a touchscreen used to do, and doing it without asking the visitor to perform.',
                    'The practical consequence is that the piece is never the same twice, and never dramatically different either. It has weather rather than states.'
                ]
            },
            {
                heading: 'Where it goes wrong',
                paragraphs: [
                    'Responsiveness becomes a gimmick the moment it becomes legible. As soon as a visitor works out the rule, the work collapses into a demonstration of that rule.',
                    'The pieces that last keep their mechanism slightly out of reach. Not hidden, not obscure, just quiet enough that you are left with the sensation instead of the explanation.'
                ]
            }
        ]
    },
    {
        slug: 'the-rise-of-experiential-minimalism-in-contemporary-exhibitions',
        date: '2025-12-03',
        dateLabel: 'December 3, 2025',
        title: 'The Rise of Experiential Minimalism in Contemporary Exhibitions',
        excerpt:
            'Instead of spectacle, galleries are embracing controlled intensity — restrained environments with powerful psychological impact.',
        author: 'Matthew Spears',
        authorPhoto: img('CagxSOp1UFUTF4ZWAXt71nQBmFk.png'),
        authorRole: 'Exhibition Designer',
        image: img('thought-quiet-gallery.jpg'),
        readMinutes: '8',
        topic: 'Exhibition',
        body: [
            {
                paragraphs: [
                    'A decade of immersive rooms taught institutions something they did not expect to learn: spectacle is easy to sell and impossible to remember. Visitors left with photographs and no impression.',
                    'The correction underway now is not a return to white walls. It is something stricter and considerably more demanding.'
                ]
            },
            {
                heading: 'Fewer objects, more pressure',
                paragraphs: [
                    'Experiential minimalism means reducing the number of things in a room until each remaining one has nowhere to hide. Four works in a space built for twenty. A single sound source. One entrance and no obvious route.',
                    'The effect is not calm. Emptiness at that scale reads as tension, and tension is what keeps a visitor present.'
                ]
            },
            {
                heading: 'Designing the interval',
                paragraphs: [
                    'The interesting decisions have moved into the gaps. How far you walk between pieces, how long the corridor is, whether you can see the next room before you reach it.',
                    'These intervals do most of the psychological work. A long approach makes a small object monumental; a short one makes the same object incidental.'
                ]
            },
            {
                heading: 'The cost of restraint',
                paragraphs: [
                    'This approach is harder to fund, because it looks like less. It photographs badly and it resists the summary that a press release wants.',
                    'What it produces is a visitor who stays for forty minutes instead of eight, and who can describe the show a month later without looking at their phone.'
                ]
            }
        ]
    },
    {
        slug: 'why-motion-first-art-is-defining-the-next-creative-era',
        date: '2025-12-04',
        dateLabel: 'December 4, 2025',
        title: 'Why Motion-First Art Is Defining The Next Creative Era',
        excerpt:
            'Static visuals are no longer enough. Motion-first thinking is reshaping art, branding, and digital experiences — here’s why the shift matters.',
        author: 'Jayson Castle',
        authorPhoto: img('aB5tmr7n4kg9KC9uwroe7fpx4.jpg'),
        authorRole: 'Visual Artist / Creator',
        image: img('thought-motion-blur.jpg'),
        readMinutes: '5',
        topic: 'Motion',
        body: [
            {
                paragraphs: [
                    'Motion used to be applied at the end. You made the thing, then you decided how it should arrive. That order is now reversed in most of the work I find worth looking at.',
                    'Designing motion first changes what you make, because timing constrains form in a way that composition never does.'
                ]
            },
            {
                heading: 'Timing is a material',
                paragraphs: [
                    'A curve has properties the way a paper stock does. A long ease-out feels expensive. A linear move feels mechanical and cheap, which is occasionally exactly what you want.',
                    'Once you accept that, you stop choosing an animation and start choosing a behaviour, then building the static frames that behaviour needs.'
                ]
            },
            {
                heading: 'Identity as behaviour',
                paragraphs: [
                    'The strongest contemporary identities are recognisable before any logo resolves. You know the network from how a wipe decelerates.',
                    'This is durable in a way that a mark is not. Marks get redrawn every five years. A timing family survives, because it is closer to a voice than to a shape.'
                ]
            },
            {
                heading: 'The discipline it demands',
                paragraphs: [
                    'Motion-first work fails loudly. A composition that is slightly wrong still reads; timing that is slightly wrong feels broken, and everybody can tell even if they cannot say why.',
                    'That intolerance is the appeal. It forces decisions to be right rather than defensible.'
                ]
            }
        ]
    },
    {
        slug: 'why-slowness-is-becoming-a-radical-artistic-choice',
        date: '2025-10-01',
        dateLabel: 'October 1, 2025',
        title: 'Why Slowness Is Becoming a Radical Artistic Choice',
        excerpt:
            'In a culture obsessed with speed, instant gratification, and constant refresh, artists are turning to slowness — not as nostalgia, but as resistance.',
        author: 'Freja Andersson',
        authorPhoto: img('vixlnXliZeBaiiCOs8q2QCgaM8.jpg'),
        authorRole: 'Artist / Writer',
        image: img('thought-slow-studio.jpg'),
        readMinutes: '6',
        topic: 'Process',
        body: [
            {
                paragraphs: [
                    'Slowness is usually framed as nostalgia, as though wanting to spend six months on one piece were a longing for a gentler decade. It is not. It is a position about attention, and it is taken deliberately.',
                    'A work that cannot be consumed quickly cannot be distributed quickly, and a work that cannot be distributed quickly has to survive on other terms.'
                ]
            },
            {
                heading: 'Duration as a filter',
                paragraphs: [
                    'When a piece demands twenty minutes, it selects its own audience. Most people leave, which sounds like failure and is actually the mechanism working.',
                    'The ones who stay engage differently. They are not deciding whether to like it; they have already paid enough attention that the question changed.'
                ]
            },
            {
                heading: 'Making at the wrong speed',
                paragraphs: [
                    'Working slowly changes the work, not just its reception. Ideas that survive four months are different from ideas that survive four days, because they have had time to stop being exciting.',
                    'What is left after the excitement drains is usually the part worth keeping.'
                ]
            },
            {
                heading: 'Resistance, not retreat',
                paragraphs: [
                    'None of this requires rejecting the tools or the platforms. Slowness is not a refusal to publish; it is a refusal to let the publishing schedule set the pace of the thinking.',
                    'That is a small distinction and it carries the whole argument.'
                ]
            }
        ]
    }
]

export const getThought = (slug: string): Thought | undefined =>
    thoughts.find((thought) => thought.slug === slug)

export const getOtherThoughts = (slug: string): readonly Thought[] =>
    thoughts.filter((thought) => thought.slug !== slug)
